import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { JobContactAttachment } from '../../types';

interface DeleteAttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: JobContactAttachment | null;
  onConfirm: (attachmentId: number, deleteReason: string) => void;
  isLoading?: boolean;
}

const DeleteAttachmentModal: React.FC<DeleteAttachmentModalProps> = ({
  isOpen,
  onClose,
  attachment,
  onConfirm,
  isLoading,
}) => {
  const [deleteReason, setDeleteReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setDeleteReason('');
    }
  }, [isOpen, attachment?.id]);

  const handleConfirm = () => {
    if (!attachment || !deleteReason.trim()) return;
    onConfirm(attachment.id, deleteReason.trim());
  };

  const handleClose = () => {
    setDeleteReason('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Delete Attachment"
      size="md"
      closeOnBackdropClick={false}
    >
      {attachment && (
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Delete Attachment
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  <p>Are you sure you want to delete this attachment?</p>
                  <p className="mt-1 font-medium break-all">{attachment.fileName}</p>
                  <p className="text-xs">Size: {attachment.fileSize ? (attachment.fileSize / 1024 / 1024).toFixed(2) : 'Unknown'} MB</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Reason for deletion <span className="text-red-500">*</span>
            </label>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Please provide a reason for deleting this attachment..."
              rows={3}
            />
            {!deleteReason.trim() && (
              <div className="text-xs text-red-600">Delete reason is required.</div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirm} 
              disabled={isLoading || !deleteReason.trim()}
            >
              {isLoading ? 'Deleting...' : 'Delete Attachment'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DeleteAttachmentModal;
